import type { StationeryTemplateCategoryEnum } from '@graphql/generated';

// ============================================================================
// Type Definitions
// ============================================================================

export type PromotionConfig = Readonly<{
  code: string;
  description?: string;
  categoryList: ReadonlyArray<string>;
  expirationDate?: string;
  discountPercentage: number;
  active: boolean;
  usesPerEvent?: number;
  color?: string;
}>;

export type ProductInfo = Readonly<{ occasion: string; category: StationeryTemplateCategoryEnum; vendor?: string }>;

export type Sale = PromotionConfig;

// ============================================================================
// Validation Functions
// ============================================================================

export const isCodeExpired = (promotion: PromotionConfig): boolean => {
  const { active, expirationDate: expiration } = promotion;

  if (!active) {
    return true;
  }

  if (!expiration) {
    // If no expiration date, code never expires, so it is valid.
    return false;
  } else {
    const expirationDate = new Date(expiration);
    const currentDate = Date.now();
    if (Number.isNaN(expirationDate.valueOf())) {
      // If code is not a valid date, fail-safe and validate anyways.
      return false;
    } else {
      return expirationDate.valueOf() < currentDate;
    }
  }
};

export const doesProductComponentMatch = (value: string, acceptableValue: string): boolean => {
  return value === acceptableValue || value === '*';
};

export const isPromoValidForVendor = (promotion: PromotionConfig, vendor: string): boolean => {
  const products = promotion.categoryList;
  return products.some(product => {
    if (!product) {
      return false;
    }

    const productTree = product.split('/');
    if (productTree.length !== 3) {
      // Strict on structure. Must be occasion/category/vendor.
      // Use wildcards to do partial matching.
      return false;
    }

    const [_, __, promoVendor] = productTree;
    return doesProductComponentMatch(promoVendor, vendor);
  });
};

/**
 * Checks if promotion applies to the current product.
 */
export const isPromoValidForProduct = (promotion: PromotionConfig, currentProductInfo: ProductInfo): boolean => {
  const products = promotion.categoryList;
  return products.some(product => {
    if (!product) {
      return false;
    }
    const productTree = product.split('/');
    if (productTree.length !== 3) {
      // Strict on structure. Must be occasion/category/vendor.
      // Use wildcards to do partial matching.
      return false;
    }
    const [occasion, category] = productTree;
    const { occasion: currentOccasion, category: currentCategory, vendor: currentVendor } = currentProductInfo;

    if (!doesProductComponentMatch(occasion, currentOccasion)) {
      return false;
    }

    if (!doesProductComponentMatch(category, currentCategory)) {
      return false;
    }

    if (currentVendor && !isPromoValidForVendor(promotion, currentVendor)) {
      return false;
    }

    return true;
  });
};

export const isPromoValid = (promotion: PromotionConfig | undefined, productInfo: ProductInfo): boolean => {
  if (!promotion) {
    return false;
  }

  if (isCodeExpired(promotion)) {
    return false;
  }

  if (!isPromoValidForProduct(promotion, productInfo)) {
    return false;
  }

  return true;
};

// ============================================================================
// Sales Payload Validation
// ============================================================================

const isValidSale = (payload: unknown): payload is Sale => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const p = payload as Record<string, unknown>;

  return typeof p.code === 'string' && Array.isArray(p.categoryList) && typeof p.discountPercentage === 'number' && typeof p.active === 'boolean';
};

export const isValidSalesPayload = (payload: unknown): payload is Sale[] => {
  if (!payload) {
    return false;
  }

  if (!Array.isArray(payload)) {
    return false;
  }

  if (payload.length <= 0) {
    return false;
  }

  if (payload.some(item => !isValidSale(item))) {
    return false;
  }

  return true;
};
